import { useState, type CSSProperties } from 'react';
import { useSeoMeta } from '@unhead/react';

const GREETINGS = [
  'Hello, world! 👋',
  'Jambo! 🦒',
  '¡Hola! 🌮',
  'Bonjour! 🥐',
  'Ciao! 🍕',
  'こんにちは! 🌸',
  '안녕하세요! 💜',
  'Olá! 🌴',
  'Hallo! 🥨',
  'Aloha! 🌺',
  'Hej! 🧇',
];

const BUTTON_GRADIENTS = [
  'bg-gradient-to-r from-pink-500 via-fuchsia-500 to-purple-500',
  'bg-gradient-to-r from-orange-400 via-rose-500 to-pink-500',
  'bg-gradient-to-r from-sky-400 via-blue-500 to-indigo-500',
  'bg-gradient-to-r from-lime-400 via-emerald-500 to-teal-500',
  'bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500',
  'bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500',
];

const CONFETTI_EMOJI = ['🎉', '✨', '🌈', '🎈', '🦄', '🍩', '🌟', '💥', '🧁', '🎊', '🍕', '🐸'];

interface Particle {
  id: number;
  emoji: string;
  dx: number;
  dy: number;
  rot: number;
  size: number;
}

const Index = () => {
  useSeoMeta({
    title: 'Hello World 🌈',
    description: 'A super simple, colorful, fun page with a hello world button.',
  });

  const [clickCount, setClickCount] = useState(0);
  const [particles, setParticles] = useState<Particle[]>([]);

  const greeting = GREETINGS[clickCount % GREETINGS.length];
  const buttonGradient = BUTTON_GRADIENTS[clickCount % BUTTON_GRADIENTS.length];

  const handleClick = () => {
    setClickCount((c) => c + 1);

    const burst: Particle[] = Array.from({ length: 14 }, (_, i) => ({
      id: Date.now() + i,
      emoji: CONFETTI_EMOJI[Math.floor(Math.random() * CONFETTI_EMOJI.length)],
      dx: (Math.random() - 0.5) * 320,
      dy: -(60 + Math.random() * 260),
      rot: (Math.random() - 0.5) * 540,
      size: 1.25 + Math.random() * 1.5,
    }));
    setParticles((p) => [...p, ...burst]);
  };

  const removeParticle = (id: number) => {
    setParticles((p) => p.filter((particle) => particle.id !== id));
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center gap-8 overflow-hidden relative motion-safe:animate-rainbow-pan"
      style={{
        backgroundImage:
          'linear-gradient(135deg, #ff9a9e, #fecfef, #a18cd1, #84fab0, #8fd3f4, #fbc2eb, #ffd1ff, #ff9a9e)',
        backgroundSize: '400% 400%',
      }}
    >
      {/* Floating emoji burst particles */}
      {particles.map((particle) => (
        <span
          key={particle.id}
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2 motion-safe:animate-emoji-pop"
          style={
            {
              '--dx': `${particle.dx}px`,
              '--dy': `${particle.dy}px`,
              '--rot': `${particle.rot}deg`,
              fontSize: `${particle.size}rem`,
            } as CSSProperties
          }
          onAnimationEnd={() => removeParticle(particle.id)}
        >
          {particle.emoji}
        </span>
      ))}

      <h1
        key={greeting}
        className="text-5xl sm:text-7xl font-extrabold text-white drop-shadow-[0_4px_12px_rgba(0,0,0,0.35)] text-center px-4 motion-safe:animate-pop-in"
      >
        {greeting}
      </h1>

      <button
        onClick={handleClick}
        className={`${buttonGradient} text-white text-2xl sm:text-3xl font-extrabold px-12 py-6 rounded-full shadow-[0_10px_30px_rgba(0,0,0,0.3)] ring-4 ring-white/50 transition-transform duration-150 hover:scale-110 active:scale-90 motion-safe:animate-wiggle cursor-pointer select-none`}
      >
        Say Hello! 👋
      </button>

      <p className="text-white/90 text-lg font-semibold drop-shadow-md">
        {clickCount === 0
          ? 'Click the button! 🎈'
          : `You've said hello ${clickCount} ${clickCount === 1 ? 'time' : 'times'}! 🎈`}
      </p>

      <a
        href="https://shakespeare.diy"
        target="_blank"
        rel="noopener noreferrer"
        className="absolute bottom-4 text-white/70 text-sm hover:text-white transition-colors"
      >
        Vibed with Shakespeare
      </a>
    </div>
  );
};

export default Index;
